import { resolveElementProps, type Spec, type StateModel, type UIElement } from "@json-render/core";
import { pgaIdFromPersonHref, type M360StructuredBlock } from "../m360StructuredContent";

const EVENT_PRIMARY_TYPES = new Set(["EventCard", "EventList"]);
const WRONG_EVENT_ANSWER_TYPES = new Set(["SectionCard", "FacilityCard", "CountSummaryCard"]);

type SpecLink = { label: string; href: string; description?: string };

function elementType(spec: Spec, key: string | undefined): string | null {
  if (!key) {
    return null;
  }

  const element = spec.elements?.[key] as UIElement | undefined;
  return element?.type ?? null;
}

/** Best-effort primary catalog element key for a spec (Stack unwraps to its first child). */
export function getSpecPrimaryElementKey(spec: Spec | null): string | null {
  if (!spec?.root) {
    return null;
  }

  const rootType = elementType(spec, spec.root);
  if (rootType !== "Stack") {
    return spec.root;
  }

  const rootElement = spec.elements?.[spec.root] as UIElement | undefined;
  return rootElement?.children?.[0] ?? null;
}

/** Best-effort primary catalog component for a spec (Stack unwraps to its first child). */
export function getSpecPrimaryComponentType(spec: Spec | null): string | null {
  if (!spec) {
    return null;
  }

  return elementType(spec, getSpecPrimaryElementKey(spec) ?? undefined);
}

function isPersonRecordProps(item: Record<string, unknown>): boolean {
  const kind = item.kind ? String(item.kind) : "";
  const href = item.href ? String(item.href) : "";
  return kind === "person" || href.startsWith("/people/");
}

function resolvedPrimaryElementProps(spec: Spec): Record<string, unknown> | null {
  const primaryKey = getSpecPrimaryElementKey(spec);
  if (!primaryKey) {
    return null;
  }

  const element = spec.elements?.[primaryKey] as UIElement | undefined;
  if (!element) {
    return null;
  }

  const stateModel: StateModel = spec.state ?? {};
  return resolveElementProps((element.props ?? {}) as Record<string, unknown>, { stateModel });
}

/** True when the spec's primary catalog surface is a PersonCard or a people RecordList. */
export function specLooksLikePeopleList(spec: Spec | null): boolean {
  if (!spec) {
    return false;
  }

  const primaryType = getSpecPrimaryComponentType(spec);
  if (primaryType === "PersonCard") {
    return true;
  }

  if (primaryType !== "RecordList") {
    return false;
  }

  const props = resolvedPrimaryElementProps(spec);
  const items = props?.items;
  if (!Array.isArray(items) || items.length === 0) {
    return false;
  }

  return items.every(
    (item) => item && typeof item === "object" && isPersonRecordProps(item as Record<string, unknown>)
  );
}

/** True when a promoted structured block is a PersonCard or an all-people RecordList. */
export function isPeopleStructuredBlock(block: M360StructuredBlock | null): block is M360StructuredBlock {
  if (!block) {
    return false;
  }

  if (block.type === "person") {
    return true;
  }

  if (block.type !== "record_list" || block.items.length === 0) {
    return false;
  }

  return block.items.every((item) => item.kind === "person" || Boolean(item.href?.startsWith("/people/")));
}

/** True when a people row already has copy or chips. Photos alone do not count — they merge onto either surface. */
function recordHasPeopleEnrichment(item: Record<string, unknown>): boolean {
  const links = Array.isArray(item.links) ? item.links : [];
  return Boolean(item.subtitle || item.meta || links.length > 0);
}

function peopleSpecItemsAreSkinny(spec: Spec): boolean {
  const primaryType = getSpecPrimaryComponentType(spec);
  const props = resolvedPrimaryElementProps(spec);
  if (!props) {
    return false;
  }

  if (primaryType === "PersonCard") {
    return !recordHasPeopleEnrichment(props);
  }

  if (primaryType !== "RecordList") {
    return false;
  }

  const items = props.items;
  if (!Array.isArray(items) || items.length === 0) {
    return false;
  }

  return items.every(
    (item) => !item || typeof item !== "object" || !recordHasPeopleEnrichment(item as Record<string, unknown>)
  );
}

function promotedPeopleAreRicher(block: M360StructuredBlock): boolean {
  if (block.type === "person") {
    return Boolean(block.subtitle || (block.links && block.links.length > 0));
  }

  if (block.type !== "record_list") {
    return false;
  }

  return block.items.some((item) => recordHasPeopleEnrichment(item as unknown as Record<string, unknown>));
}

/**
 * SearchPeople markdown is authoritative when json-render emitted name/href-only people rows.
 * Do not replace a people spec that already has subtitle, email (meta), or chips.
 * Photos (imageUrl) merge onto whichever surface wins and do not make a spec "rich."
 * Mixed lists (one rich row, one skinny) stay on the spec — fill skinny rows via
 * `mergeDumpFieldsIntoSkinnyPeopleSpec` instead of replacing the whole list.
 */
export function shouldPreferPeopleDumpOverSpec(spec: Spec | null, promotedBlock: M360StructuredBlock | null): boolean {
  if (!spec || !isPeopleStructuredBlock(promotedBlock) || !specLooksLikePeopleList(spec)) {
    return false;
  }

  return peopleSpecItemsAreSkinny(spec) && promotedPeopleAreRicher(promotedBlock);
}

function stateKeyFromPointer(pointer: string): string | null {
  const key = pointer.replace(/^\//, "").split("/")[0];
  return key || null;
}

function peopleIdentityKeys(item: Record<string, unknown>): string[] {
  const keys: string[] = [];
  const href = item.href ? String(item.href) : "";
  const pgaId = item.pgaId ? String(item.pgaId) : pgaIdFromPersonHref(href);
  if (pgaId) {
    keys.push(pgaId);
  }
  if (href) {
    keys.push(href);
  }
  return keys;
}

function rememberDumpItem(map: Map<string, Record<string, unknown>>, item: Record<string, unknown>): void {
  for (const key of peopleIdentityKeys(item)) {
    if (!map.has(key)) {
      map.set(key, item);
    }
  }
}

function dumpFieldsByIdentity(block: M360StructuredBlock): Map<string, Record<string, unknown>> {
  const map = new Map<string, Record<string, unknown>>();

  if (block.type === "person") {
    rememberDumpItem(map, {
      pgaId: block.pgaId,
      href: `/people/${block.pgaId}/person/overview`,
      subtitle: block.subtitle,
      links: block.links,
      imageUrl: block.imageUrl
    });
    return map;
  }

  if (block.type !== "record_list") {
    return map;
  }

  for (const item of block.items) {
    rememberDumpItem(map, item as unknown as Record<string, unknown>);
  }

  return map;
}

function dumpFieldsForItem(
  item: Record<string, unknown>,
  dumpByIdentity: Map<string, Record<string, unknown>>
): Record<string, unknown> | null {
  for (const key of peopleIdentityKeys(item)) {
    const dump = dumpByIdentity.get(key);
    if (dump) {
      return dump;
    }
  }

  return null;
}

function fillSkinnyPeopleRowFromDump(
  item: Record<string, unknown>,
  dumpByIdentity: Map<string, Record<string, unknown>>
): { next: Record<string, unknown>; changed: boolean } {
  if (recordHasPeopleEnrichment(item)) {
    return { next: item, changed: false };
  }

  const dump = dumpFieldsForItem(item, dumpByIdentity);
  if (!dump) {
    return { next: item, changed: false };
  }

  const next = { ...item };
  let changed = false;

  if (!next.subtitle && dump.subtitle) {
    next.subtitle = dump.subtitle;
    changed = true;
  }
  if (!next.meta && dump.meta) {
    next.meta = dump.meta;
    changed = true;
  }

  const existingLinks = Array.isArray(next.links) ? next.links : [];
  const dumpLinks = Array.isArray(dump.links) ? dump.links : [];
  if (existingLinks.length === 0 && dumpLinks.length > 0) {
    next.links = dumpLinks;
    changed = true;
  }

  if (!next.imageUrl && dump.imageUrl) {
    next.imageUrl = dump.imageUrl;
    changed = true;
  }

  return { next, changed };
}

/**
 * Keep a mixed people spec (some rows already have copy/chips) and copy SearchPeople
 * dump fields onto only the name/href-only rows, matched by PGA ID or href.
 */
export function mergeDumpFieldsIntoSkinnyPeopleSpec(
  spec: Spec | null,
  promotedBlock: M360StructuredBlock | null
): Spec | null {
  if (!spec?.elements || !isPeopleStructuredBlock(promotedBlock) || !specLooksLikePeopleList(spec)) {
    return spec;
  }

  if (!promotedPeopleAreRicher(promotedBlock)) {
    return spec;
  }

  const dumpByIdentity = dumpFieldsByIdentity(promotedBlock);
  if (dumpByIdentity.size === 0) {
    return spec;
  }

  let changed = false;
  const elements = { ...spec.elements };
  const state = spec.state ? { ...spec.state } : spec.state;

  for (const [key, element] of Object.entries(elements)) {
    const typedElement = element as UIElement;
    if (typedElement.type === "PersonCard") {
      const props = { ...(typedElement.props ?? {}) } as Record<string, unknown>;
      const result = fillSkinnyPeopleRowFromDump(props, dumpByIdentity);
      if (!result.changed) {
        continue;
      }

      elements[key] = {
        ...typedElement,
        props: result.next
      };
      changed = true;
      continue;
    }

    if (typedElement.type !== "RecordList") {
      continue;
    }

    const rawItems = typedElement.props?.items;
    if (Array.isArray(rawItems)) {
      let itemsChanged = false;
      const nextItems = rawItems.map((item) => {
        if (!item || typeof item !== "object") {
          return item;
        }

        const result = fillSkinnyPeopleRowFromDump(item as Record<string, unknown>, dumpByIdentity);
        if (result.changed) {
          itemsChanged = true;
        }
        return result.next;
      });

      if (itemsChanged) {
        elements[key] = {
          ...typedElement,
          props: {
            ...(typedElement.props ?? {}),
            items: nextItems
          }
        };
        changed = true;
      }
      continue;
    }

    if (!rawItems || typeof rawItems !== "object" || !("$state" in rawItems) || !state) {
      continue;
    }

    const stateKey = stateKeyFromPointer(String((rawItems as { $state: string }).$state));
    const stateItems = stateKey ? state[stateKey] : undefined;
    if (!stateKey || !Array.isArray(stateItems)) {
      continue;
    }

    let itemsChanged = false;
    const nextItems = stateItems.map((item) => {
      if (!item || typeof item !== "object") {
        return item;
      }

      const result = fillSkinnyPeopleRowFromDump(item as Record<string, unknown>, dumpByIdentity);
      if (result.changed) {
        itemsChanged = true;
      }
      return result.next;
    });

    if (itemsChanged) {
      state[stateKey] = nextItems;
      changed = true;
    }
  }

  return changed ? { ...spec, elements, ...(state ? { state } : {}) } : spec;
}

export function isEventStructuredBlock(block: M360StructuredBlock | null): block is M360StructuredBlock {
  if (!block || block.type !== "record_list" || block.items.length === 0) {
    return false;
  }

  return block.items.every((item) => item.kind === "event" || item.href?.startsWith("/events/"));
}

/** When tool output includes events but the model rendered a section/facility card instead. */
export function shouldPreferEventCardsOverSpec(spec: Spec | null, promotedBlock: M360StructuredBlock | null): boolean {
  if (!isEventStructuredBlock(promotedBlock)) {
    return false;
  }

  const primaryType = getSpecPrimaryComponentType(spec);
  if (!primaryType || EVENT_PRIMARY_TYPES.has(primaryType)) {
    return false;
  }

  return WRONG_EVENT_ANSWER_TYPES.has(primaryType);
}

function eventRowPropsLackFacilityContext(props: Record<string, unknown>): boolean {
  return !props.facilityName && !props.facilityHref && !props.location;
}

function collectEventRowPropsFromSpec(spec: Spec): Record<string, unknown>[] {
  const rows: Record<string, unknown>[] = [];
  const stateModel: StateModel = spec.state ?? {};
  const ctx = { stateModel };

  const collectFromElement = (key: string | undefined) => {
    if (!key) {
      return;
    }

    const element = spec.elements?.[key] as UIElement | undefined;
    if (!element) {
      return;
    }

    if (element.type === "EventCard") {
      const props = resolveElementProps((element.props ?? {}) as Record<string, unknown>, ctx);
      rows.push(props);
      return;
    }

    if (element.type === "EventList") {
      const props = resolveElementProps((element.props ?? {}) as Record<string, unknown>, ctx);
      const items = props.items;
      if (Array.isArray(items)) {
        for (const item of items) {
          if (item && typeof item === "object") {
            rows.push(item as Record<string, unknown>);
          }
        }
      }
    }
  };

  collectFromElement(spec.root ?? undefined);
  const rootElement = spec.root ? (spec.elements?.[spec.root] as UIElement | undefined) : undefined;
  rootElement?.children?.forEach(collectFromElement);

  return rows;
}

/** True when the primary EventList resolved to an explicit empty array. */
function specPrimaryEventListIsExplicitlyEmpty(spec: Spec): boolean {
  const primaryKey = getSpecPrimaryElementKey(spec);
  const primaryType = getSpecPrimaryComponentType(spec);
  if (primaryType !== "EventList" || !primaryKey) {
    return false;
  }

  const element = spec.elements?.[primaryKey] as UIElement | undefined;
  if (!element) {
    return false;
  }

  const stateModel: StateModel = spec.state ?? {};
  const props = resolveElementProps((element.props ?? {}) as Record<string, unknown>, { stateModel });
  const items = props.items;
  return Array.isArray(items) && items.length === 0;
}

/** True when EventCard/EventList rows omit facilityName, facilityHref, and location. */
function specEventCardsLackFacilityContext(spec: Spec): boolean {
  const rows = collectEventRowPropsFromSpec(spec);
  return rows.length > 0 && rows.some(eventRowPropsLackFacilityContext);
}

/** True when promoted tool-result rows actually supply facilityName, facilityHref, or location. */
function promotedEventsAddFacilityContext(block: M360StructuredBlock): boolean {
  if (block.type !== "record_list") {
    return false;
  }

  return block.items.some((item) => Boolean(item.facilityName || item.facilityHref || item.location));
}

/**
 * SearchEvents markdown is authoritative when it adds facility/location the model omitted.
 * Do not replace an EventCard/EventList that already has useful status/dates with link-only tool-results.
 */
export function shouldPreferToolResultEventsOverSpec(
  spec: Spec | null,
  promotedBlock: M360StructuredBlock | null
): boolean {
  if (!spec || !isEventStructuredBlock(promotedBlock)) {
    return false;
  }

  const primaryType = getSpecPrimaryComponentType(spec);
  if (!primaryType || !EVENT_PRIMARY_TYPES.has(primaryType)) {
    return false;
  }

  if (specPrimaryEventListIsExplicitlyEmpty(spec)) {
    return true;
  }

  return specEventCardsLackFacilityContext(spec) && promotedEventsAddFacilityContext(promotedBlock);
}

export function hasRenderableStructuredBlock(block: M360StructuredBlock | null): boolean {
  if (!block) {
    return false;
  }

  if (block.type !== "record_list") {
    return true;
  }

  return block.items.length > 0;
}

function normalizeSpecLinks(links: unknown): SpecLink[] | undefined {
  if (!Array.isArray(links)) {
    return undefined;
  }

  const normalized: SpecLink[] = [];
  for (const link of links) {
    const item = link as { label?: string; href?: string; description?: string | null };
    if (!item.label || !item.href) {
      continue;
    }

    normalized.push({
      label: item.label,
      href: item.href,
      ...(item.description ? { description: item.description } : {})
    });
  }

  return normalized.length > 0 ? normalized : undefined;
}

function collectSpecEventLinks(spec: Spec): Map<string, SpecLink[]> {
  const linksByKey = new Map<string, SpecLink[]>();

  const rememberLinks = (key: string | undefined, links: SpecLink[] | undefined) => {
    if (!key || !links?.length) {
      return;
    }

    linksByKey.set(key, links);
  };

  const collectFromProps = (props: Record<string, unknown>) => {
    const href = props.href ? String(props.href) : undefined;
    const title = props.title ? String(props.title) : undefined;
    const links = normalizeSpecLinks(props.links);
    rememberLinks(href, links);
    rememberLinks(title, links);

    const items = props.items;
    if (Array.isArray(items)) {
      for (const item of items) {
        const row = item as Record<string, unknown>;
        collectFromProps(row);
      }
    }
  };

  const collectFromElement = (key: string | undefined) => {
    if (!key) {
      return;
    }

    const element = spec.elements?.[key] as UIElement | undefined;
    if (!element) {
      return;
    }

    if (element.type === "EventCard" || element.type === "EventList") {
      collectFromProps(element.props as Record<string, unknown>);
    }
  };

  collectFromElement(spec.root ?? undefined);
  const rootElement = spec.root ? (spec.elements?.[spec.root] as UIElement | undefined) : undefined;
  rootElement?.children?.forEach(collectFromElement);

  return linksByKey;
}

/** Keep footer chips from EventCard/EventList specs when tool-results replace the rendered spec. */
export function mergeSpecLinksIntoPromotedBlock(
  spec: Spec | null,
  promotedBlock: M360StructuredBlock | null
): M360StructuredBlock | null {
  if (!spec || !promotedBlock || promotedBlock.type !== "record_list") {
    return promotedBlock;
  }

  const specLinksByKey = collectSpecEventLinks(spec);
  if (specLinksByKey.size === 0) {
    return promotedBlock;
  }

  const mergeLinks = (
    existing: { label: string; href: string; description?: string | null }[] | undefined,
    fromSpec: SpecLink[]
  ) => {
    const merged: SpecLink[] = (existing ?? []).map((link) => ({
      label: link.label,
      href: link.href,
      description: link.description ?? undefined
    }));
    for (const link of fromSpec) {
      if (!merged.some((candidate) => candidate.href === link.href && candidate.label === link.label)) {
        merged.push(link);
      }
    }

    return merged.length > 0 ? merged : undefined;
  };

  const items = promotedBlock.items.map((item) => {
    const fromSpec =
      (item.href ? specLinksByKey.get(item.href) : undefined) ??
      (item.title ? specLinksByKey.get(item.title) : undefined);
    if (!fromSpec) {
      return item;
    }

    return {
      ...item,
      links: mergeLinks(item.links, fromSpec)
    };
  });

  return { ...promotedBlock, items };
}
